import styled from '@emotion/styled';
import classNames from 'classnames';
import throttle from 'lodash-es/throttle';
import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { semantic_colors } from '../../foundation';
import { IconPlus, IconProps } from '../icons/generated';
import { Stack } from '../stack';

export interface BoxTabsContextProps {
  activeTabId?: string;
  onChange?: (id: string) => void;
}

export const BoxTabContext = createContext<BoxTabsContextProps | null>(null);

export const useBoxTabContext = () => {
  const context = useContext(BoxTabContext);

  if (!context) {
    throw new Error('BoxTabContext is not defined');
  }
  return context;
};

type BaseBoxTabsProps = {
  className?: string;
  /**
   * 활성화된 탭 ID
   */
  activeTabId?: string;
  /**
   * 추가 버튼 표시 여부
   * @default false
   * @deprecated onAdd 혹은 renderAddAction 중 하나를 사용해주세요.
   */
  showAddButton?: boolean;
  /**
   *
   * 탭 변경 이벤트
   * 변경된 탭 ID
   */
  onChange: (id: string) => void;
};

type AddActionProps =
  | {
      /**
       * 추가 버튼 클릭 이벤트
       */
      onAdd: () => void;
      renderAddAction?: never;
    }
  | {
      onAdd?: never;
      /**
       * 커스텀 액션 렌더링 함수
       */
      renderAddAction: (action: {
        iconComponent: React.ComponentType<IconProps>;
        iconProps: IconProps;
      }) => React.ReactNode;
    }
  | {
      onAdd?: never;
      renderAddAction?: never;
    };

export type BoxTabsProps = BaseBoxTabsProps & AddActionProps;

export const BoxTabs = ({
  className,
  children,
  activeTabId,
  onChange,
  onAdd,
  renderAddAction,
}: PropsWithChildren<BoxTabsProps>) => {
  const tabListRef = useRef<HTMLElement | null>(null);
  const scrollWrapperRef = useRef<HTMLDivElement | null>(null);
  const initialRenderRef = useRef(true);

  const [mask, setMask] = useState({ leftMask: 0, rightMask: 0 });
  const [isOverflowing, setIsOverflowing] = useState(false);

  // showAddButton prop 여부와 상관 없이 onAdd 혹은 renderAddAction이 있는 경우 버튼이 노출 됩니다.
  const showAddButton = typeof onAdd === 'function' || typeof renderAddAction === 'function';

  const updateMask = useCallback(() => {
    const scrollContainer = scrollWrapperRef.current;
    if (!scrollContainer) return;

    const { scrollLeft, scrollWidth, offsetWidth } = scrollContainer;

    const hasOverflow = scrollWidth > (tabListRef.current?.offsetWidth ?? 0);
    setIsOverflowing(hasOverflow);

    if (hasOverflow) {
      const showLeftMask = scrollLeft > 0;
      const showRightMask = scrollLeft + offsetWidth < scrollWidth - 1;

      setMask({
        leftMask: showLeftMask ? 1 : 0,
        rightMask: showRightMask ? 1 : 0,
      });
    } else {
      setMask({ leftMask: 0, rightMask: 0 });
    }
  }, []);

  const handleTabScroll = throttle(() => {
    updateMask();
  }, 300);

  const updateActiveScrollPosition = useCallback(
    (initial: boolean) => {
      const scrollContainer = scrollWrapperRef.current;
      const tabListContainer = tabListRef.current;

      if (scrollContainer && tabListContainer && activeTabId) {
        const activeTabElement = scrollContainer.querySelector(`#${activeTabId}`) as HTMLElement;

        if (activeTabElement) {
          const tabContainerRect = tabListContainer.getBoundingClientRect();
          const currentTabItemRect = activeTabElement.getBoundingClientRect();

          const correction =
            scrollContainer.scrollLeft - tabListContainer.clientWidth / 2 + currentTabItemRect.width / 2;

          const PADDING_OFFSET = 16;
          const startLeft = initial ? currentTabItemRect.left + PADDING_OFFSET : currentTabItemRect.left;

          scrollContainer.scrollTo({
            left: startLeft - tabContainerRect.left + correction,
            behavior: initial ? 'auto' : 'smooth',
          });
        }
      }
    },
    [activeTabId],
  );

  useEffect(() => {
    updateActiveScrollPosition(initialRenderRef.current);
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
    }
    updateMask();
  }, [updateActiveScrollPosition, updateMask]);

  useEffect(() => {
    updateActiveScrollPosition(false);
  }, [activeTabId, updateActiveScrollPosition]);

  return (
    <BoxTabContext.Provider value={{ activeTabId, onChange }}>
      <TabContainer className={classNames('pds-box-tabs', className)} ref={tabListRef}>
        <Mask style={{ left: 0, opacity: mask.leftMask }} position='left' />
        <Mask style={{ right: showAddButton ? 34 : 0, opacity: mask.rightMask }} position='right' />
        <ScrollWrapper
          ref={scrollWrapperRef}
          $padding={showAddButton ? '8px 38px 0 0' : '8px 0 0 0'}
          role='tablist'
          onScroll={handleTabScroll}
        >
          <Stack gap={2}>
            {children}
            {showAddButton && (
              <>
                {mask.rightMask && isOverflowing ? <AddButtonBackground /> : null}
                <AddButton onClick={onAdd} aria-label='Add tab' $isFixed={isOverflowing}>
                  {typeof renderAddAction === 'function' ? (
                    renderAddAction({
                      iconComponent: IconPlus,
                      iconProps: { size: 16, color: semantic_colors.content.primary },
                    })
                  ) : (
                    <IconPlus size={16} color={semantic_colors.content.primary} />
                  )}
                </AddButton>
              </>
            )}
          </Stack>
        </ScrollWrapper>
      </TabContainer>
    </BoxTabContext.Provider>
  );
};

const TabContainer = styled.nav`
  position: relative;
  width: 100%;
  border-bottom: 1px solid ${semantic_colors.border.primary};
  background-color: ${semantic_colors.background.surface};
`;

const ScrollWrapper = styled.div<{ $padding: string }>`
  display: flex;
  align-items: flex-end;
  padding: ${({ $padding }) => $padding};
  margin-bottom: -1px;
  z-index: 2;
  overflow-x: auto;
  scrollbar-width: none;
  background-color: transparent;

  ::-webkit-scrollbar {
    display: none;
  }
`;

const AddButton = styled.button<{ $isFixed?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 37px;

  border: 1px solid ${semantic_colors.border.primary};
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  background-color: ${semantic_colors.background.surface};
  cursor: pointer;
  z-index: 10;
  transition: background-color 0.1s linear;

  &::after {
    content: '';
    position: absolute;
    width: 36px;
    right: -1px;
    bottom: 0;
    height: 1px;
    background-color: ${semantic_colors.border.primary};
  }

  ${({ $isFixed }) =>
    $isFixed &&
    `
    position: absolute;
    right: 0;
    bottom: -1px;
  `}

  &:hover {
    background-color: ${semantic_colors.fill.tertiary};
  }

  &:active {
    background-color: ${semantic_colors.fill.secondary};
  }

  &:disabled {
    cursor: not-allowed;
    background-color: ${semantic_colors.background.disabled};
  }
`;

const AddButtonBackground = styled.div`
  position: absolute;
  width: 38px;
  height: 38px;
  right: -2px;
  top: 8px;
  background-color: ${semantic_colors.background.surface};
  z-index: 3;
`;

const Mask = styled.div<{ position: 'left' | 'right' }>`
  position: absolute;
  height: 100%;
  width: 32px;
  pointer-events: none;
  z-index: 5;
  background: linear-gradient(
    ${({ position }) => (position === 'left' ? '90deg' : '270deg')},
    ${semantic_colors.background.surface} 0%,
    rgba(255, 255, 255, 0) 100%
  );
`;
